import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './hooks/useAuth';
import AppPreloader from './components/common/AppPreloader';
import { prefetchAllData } from './utils/prefetchData';
import { STORAGE_KEYS } from './utils/constants';
import Dashboard from './pages/dashboard/Dashboard';
import ActivityTracker from './components/common/ActivityTracker';
import './App.css';

// Загружаем Login сразу, чтобы не было прелоадера при переходе на страницу логина
import Login from './pages/auth/Login';

const App: React.FC = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const queryClient = useQueryClient();

  const [isPrefetching, setIsPrefetching] = useState(false);
  const hasPrefetchedRef = useRef(false);
  const prevAuthenticatedRef = useRef<boolean | null>(null);

  // Сбрасываем флаг предзагрузки при изменении статуса авторизации
  useEffect(() => {
    if (prevAuthenticatedRef.current === false && isAuthenticated) {
      // Пользователь только что залогинился - сбрасываем флаг
      hasPrefetchedRef.current = false;
      // Очищаем флаг prefetch из localStorage
      localStorage.removeItem(STORAGE_KEYS.PREFETCH_COMPLETED);
    }
    prevAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  // Предзагружаем все данные при первой загрузке приложения после авторизации
  // (только если prefetch не был запущен в Login компоненте)
  useEffect(() => {
    // Проверяем, был ли prefetch уже выполнен в Login компоненте
    const prefetchCompleted = localStorage.getItem(STORAGE_KEYS.PREFETCH_COMPLETED) === 'true';
    
    if (!loading && isAuthenticated && !isPrefetching && !hasPrefetchedRef.current && !prefetchCompleted) {
      hasPrefetchedRef.current = true;
      setIsPrefetching(true);
      // Предзагружаем все данные, включая пользователей дашборда
      prefetchAllData(queryClient).finally(() => {
        setIsPrefetching(false);
        localStorage.setItem(STORAGE_KEYS.PREFETCH_COMPLETED, 'true');
      });
    } else if (prefetchCompleted) {
      // Если prefetch уже был выполнен, просто устанавливаем флаг
      hasPrefetchedRef.current = true;
    }
  }, [loading, isAuthenticated, queryClient, isPrefetching]);

  // Прелоадер при проверке авторизации или при предзагрузке данных
  const hasToken = typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  if ((loading && hasToken) || (isAuthenticated && isPrefetching)) {
    return <AppPreloader />;
  }

  return (
    <BrowserRouter>
      <ActivityTracker enabled={isAuthenticated && !isPrefetching && (hasPrefetchedRef.current || localStorage.getItem(STORAGE_KEYS.PREFETCH_COMPLETED) === 'true')} userId={user?.id} />
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
        />
        <Route
          path="/*"
          element={
            isAuthenticated ? <Dashboard /> : <Navigate to="/login" replace />}
        />
      </Routes>
    </BrowserRouter>
  );
};

export default App;

